// eventBus.ts
import { reactive } from 'vue';

export type GotoPayload = { path: string; returnTo?: string | null; replace?: boolean };
type Listener = (payload?: unknown) => void;
type AnyListener = (payload?: unknown) => void;
type EmitOptions = { bubble?: boolean };

type EventBus = {
  on(event: 'goto' | string, callback: ((payload: GotoPayload) => void) | AnyListener): void;

  off(event: 'goto' | string, callback: ((payload: GotoPayload) => void) | AnyListener): void;

  emit(event: 'goto' | string, payload: GotoPayload | unknown, options?: EmitOptions): void;

  listeners: Record<string, AnyListener[]>;

  emit(event: string, payload?: unknown): void;
};

export const eventBus = reactive<EventBus>({
  listeners: {},

  on(event: string, callback: Listener) {
    (this.listeners[event] ??= []).push(callback);
  },

  off(event: string, callback: Listener) {
    const bucket = this.listeners[event];
    if (!bucket) return;
    this.listeners[event] = bucket.filter((cb) => cb !== callback);
    if (this.listeners[event].length === 0) delete this.listeners[event];
  },

  emit(event: string, payload?: unknown, options?: EmitOptions) {
    const bubble = options?.bubble ?? true;

    // Emit exact event
    const bucket = this.listeners[event];
    if (bucket?.length) bucket.forEach((listener) => listener(payload));

    // Optional: bubble to base namespace (e.g. "update:ticket" -> "update")
    if (bubble) {
      const idx = event.indexOf(':');
      if (idx > -1) {
        const base = event.slice(0, idx);
        const baseBucket = this.listeners[base];
        if (baseBucket?.length) baseBucket.forEach((listener) => listener(payload));
      }
    }
  },
});
